import { useAuth } from "#/hook/useAuth";
import { useClient } from "#/hook/useClient";
import { UserRole } from "#/types/user";
import { getFirstAllowedRoute } from "#/utils/routeUtils";
import { Loader } from "@mantine/core";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

export default function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isLoggedIn, isBootstrapped, user } = useAuth();
  const { client } = useClient();
  const location = useLocation();

  if (!isBootstrapped) {
    return (
      <div className="w-screen h-screen flex items-center justify-center">
        <Loader size={"xl"} color="blue" />
      </div>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  // Check route permissions for non-admin users
  if (user?.role !== UserRole.ADMIN && client?.allowedRoutes && client.allowedRoutes.length > 0) {
    const currentPath = location.pathname;

    // Skip check for /unauthorized route to avoid infinite redirect
    if (currentPath === "/unauthorized") {
      return <>{children}</>;
    }

    // T-Rex AI isn't part of allowedRoutes — it's gated by the assistant's own
    // per-user config, which the page checks itself and redirects if disabled.
    if (currentPath === "/t-rex") {
      return <>{children}</>;
    }

    // Check if the current path or any parent path is in allowedRoutes
    const isAllowed = client.allowedRoutes.some((allowedRoute) => {
      // Exact match
      if (currentPath === allowedRoute) return true;
      // Current path is a child of an allowed route
      if (currentPath.startsWith(allowedRoute + "/")) return true;
      return false;
    });

    if (!isAllowed) {
      // If user is trying to access dashboard (or root) but doesn't have permission,
      // redirect to the first allowed route instead of showing unauthorized
      if (currentPath === "/dashboard" || currentPath === "/") {
        return <Navigate to={getFirstAllowedRoute(client.allowedRoutes)} replace />;
      }

      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}
